import type { SectionId } from './config'

/**
 * The field registry drives the section forms, progress, the review screen and import
 * validation. Every editable leaf in a section document is listed here once.
 *
 * `scope` says what the field belongs to, which tells the UI what to iterate and tells
 * computeProgress how to expand the registry into concrete field paths for one client.
 */
export type FieldKind = 'text' | 'textarea' | 'url' | 'email' | 'phone' | 'select' | 'schedule' | 'promotions' | 'status'
export type FieldScope = 'brand' | 'location' | 'account' | 'drive' | 'brandKit' | 'keyLink' | 'special' | 'faq' | 'global'

export type FieldDef = {
  key: string
  label: string
  kind: FieldKind
  required: boolean
  /** Show the "Looks right" control when prefilled. Default true. */
  confirmable?: boolean
  hint?: string
  placeholder?: string
  /** Only applies for accounts: required only when the platform is managed. */
  requiredWhenManaged?: boolean
  /** The client supplies this (e.g. granting access); staff prefill neither fills it nor counts it. */
  clientProvides?: boolean
}

export type SectionDef = {
  id: SectionId
  number: string
  title: string
  /** Short question-like heading shown above the form. */
  heading: string
  clientIntro: string
  staffIntro: string
  optional: boolean
  scope: FieldScope
  fields: FieldDef[]
  /** Wording for a client with exactly one brand, where the default assumes several. */
  single?: Partial<Pick<SectionDef, 'title' | 'heading' | 'clientIntro'>>
}

/** The section's client-facing wording for a client with `brandCount` brands. Staff intros never change. */
export function resolveSectionCopy<T extends SectionDef>(def: T, brandCount: number): T {
  if (brandCount !== 1 || !def.single) return def
  return { ...def, ...def.single, single: def.single }
}

export const SECTIONS: Record<SectionId, SectionDef> = {
  brands: {
    id: 'brands', number: '01', title: 'Brand profiles', heading: 'Each concept, in your words.',
    clientIntro: 'We drafted these from your websites, socials and our conversations. Read each one and tap “Looks right”, or edit it first. Captions, hashtags and emoji voice are ours to handle.',
    single: {
      title: 'Brand profile', heading: 'Your brand, in your words.',
      clientIntro: 'We drafted this from your website, socials and our conversations. Read it and tap “Looks right”, or edit it first. Captions, hashtags and emoji voice are ours to handle.',
    },
    staffIntro: 'Fill this from discovery. The client sees each field marked “Pre-filled by Sonder Media House” and confirms it.',
    optional: false, scope: 'brand',
    fields: [
      { key: 'name', label: 'Brand name', kind: 'text', required: true },
      { key: 'legal', label: 'Legal entity', kind: 'text', required: false, hint: 'For contracts and invoices.' },
      { key: 'industry', label: 'Concept and cuisine', kind: 'text', required: true, placeholder: 'Restaurant / bar · Modern American' },
      { key: 'website', label: 'Website', kind: 'url', required: true, placeholder: 'https://' },
      { key: 'background', label: 'Background', kind: 'textarea', required: true, hint: 'The story, the room, the signatures. A paragraph is plenty.' },
      { key: 'tone', label: 'Tone of voice', kind: 'text', required: true, placeholder: 'Warm, community-minded, never stuffy' },
      { key: 'themes', label: 'Signature elements', kind: 'text', required: false, placeholder: 'The island bar, brunch, live music' },
      { key: 'avoid', label: 'Things to avoid', kind: 'textarea', required: false, confirmable: false, hint: 'Words, framings or topics we should never use.' },
    ],
  },
  locations: {
    id: 'locations', number: '02', title: 'Locations & hours', heading: 'Where, and when.',
    clientIntro: 'Confirm each schedule or fix what has changed. Add a separate schedule for anything with different hours: kitchen, bar, brunch. Deals like happy hour or Taco Tuesday go under Specials & events.',
    staffIntro: 'Build each location’s regular hours from Google Business Profile, then add kitchen, bar or brunch schedules where they differ. Happy hour and other deals go under Specials & events.',
    optional: false, scope: 'location',
    fields: [
      { key: 'name', label: 'Location name', kind: 'text', required: true },
      { key: 'address', label: 'Address', kind: 'textarea', required: true },
      /** Older per-location number; the location's main line now comes from Contacts. Kept for imports. */
      { key: 'phone', label: 'Phone', kind: 'phone', required: false },
      /** Only for a location marked not open yet; then it is required and Hours is not. */
      { key: 'opening', label: 'Expected opening', kind: 'text', required: false, placeholder: 'January 2028', hint: 'A month, a season or “To be announced” is fine.' },
      { key: 'schedules', label: 'Hours', kind: 'schedule', required: true },
      { key: 'extra', label: 'Notes on hours', kind: 'textarea', required: false, hint: 'Seasonal changes, holiday closures, anything unusual.' },
    ],
  },
  specials: {
    id: 'specials', number: '03', title: 'Specials & events', heading: 'What’s on, and when.',
    clientIntro: 'Deals and themed nights we can post about, like Taco Tuesday, ladies’ night or a wine dinner, then the events and milestones worth planning around. Opening hours and service times such as brunch stay in Locations & hours. Tell us whenever these change.',
    staffIntro: 'Add each location’s specials and promotions, then the events and milestones we should plan content around. Opening hours and service times stay in Locations & hours.',
    optional: true, scope: 'special',
    fields: [
      { key: 'promotions', label: 'Specials & promotions', kind: 'promotions', required: false },
      { key: 'events', label: 'Recurring events, seasonal moments, milestones', kind: 'textarea', required: false, hint: 'Live music nights, brunch series, openings, holiday blackouts. Anything we should plan content around.' },
    ],
  },
  contacts: {
    id: 'contacts', number: '04', title: 'Contact information', heading: 'Who to call, and for what.',
    clientIntro: 'Each phone number and inbox, once, tagged with what it’s for: your main line, reservations, private events, press. Your locations and FAQ answers point here, so when a number changes you only change it here.',
    staffIntro: 'Add each number and inbox once and tag what it’s for. Locations show their main line from here. On the FAQ, each question is assigned its contacts; tags here suggest who fits.',
    optional: false, scope: 'global',
    fields: [
      { key: 'phone', label: 'Phone', kind: 'phone', required: false },
      { key: 'email', label: 'Email', kind: 'email', required: false },
      { key: 'name', label: 'Name', kind: 'text', required: false },
      { key: 'role', label: 'Title', kind: 'text', required: false },
    ],
  },
  audience: {
    id: 'audience', number: '05', title: 'Audience', heading: 'Who comes in, and who’s next.',
    clientIntro: 'One profile per concept. Short answers are fine; we refine this together.',
    single: { clientIntro: 'Short answers are fine; we refine this together.' },
    staffIntro: 'One profile per concept, from discovery. Short answers are fine.',
    optional: false, scope: 'brand',
    fields: [
      { key: 'age', label: 'Age range', kind: 'text', required: true, placeholder: '25–45' },
      { key: 'price', label: 'Price positioning', kind: 'text', required: true, placeholder: 'Upscale and intentional. Never discounted.' },
      { key: 'regulars', label: 'Who comes in today', kind: 'textarea', required: true },
      { key: 'grow', label: 'Who you want more of', kind: 'textarea', required: true },
      { key: 'occasions', label: 'Occasions', kind: 'text', required: false, placeholder: 'Date night, brunch, private dining' },
      { key: 'neighborhoods', label: 'Neighborhoods', kind: 'text', required: false },
    ],
  },
  social: {
    id: 'social', number: '06', title: 'Platforms', heading: 'Connect your platforms.',
    clientIntro: 'We found your pages, profiles and listings; confirm they are the right ones. For the platforms we manage, there is one more step: Meta partner access for Instagram and Facebook, plus the Instagram login, and a manager invite or shared login for the rest.',
    staffIntro: 'Enter the handles you found and choose which platforms we manage. The client grants Meta access and adds logins only where you ask for them.',
    optional: false, scope: 'account',
    fields: [
      { key: 'handle', label: 'Handle or link', kind: 'text', required: true },
      { key: 'status', label: 'Access status', kind: 'status', required: false, requiredWhenManaged: true, confirmable: false, clientProvides: true },
      { key: 'login', label: 'Login email or username', kind: 'text', required: false, confirmable: false },
      { key: 'notes', label: 'Notes', kind: 'text', required: false, confirmable: false },
    ],
  },
  assets: {
    id: 'assets', number: '07', title: 'Assets & brand kit', heading: 'Where your content lives.',
    clientIntro: 'Share the folders holding your existing photos, video and brand files, and give hello@sondermediahouse.com upload access. Paste links or upload; nothing needs re-uploading twice.',
    staffIntro: 'Add any folders the client has already shared, or upload files. Uploads are sorted by type; change any that land in the wrong group.',
    optional: false, scope: 'drive',
    fields: [
      { key: 'label', label: 'Folder', kind: 'text', required: false, placeholder: 'Google Drive · Photos 2025' },
      { key: 'url', label: 'Link', kind: 'url', required: true },
      { key: 'access', label: 'How we get access', kind: 'text', required: false, placeholder: 'Shared with hello@sondermediahouse.com' },
    ],
  },
  links: {
    id: 'links', number: '08', title: 'Important links', heading: 'Where to send people.',
    clientIntro: 'Confirm these links are current: menus, reservations, ordering and anything else we point people to. Tell us whenever they change.',
    staffIntro: 'Fill the key links from each brand’s site.',
    optional: false, scope: 'keyLink',
    fields: [
      { key: 'food', label: 'Food menu', kind: 'url', required: true },
      { key: 'drinks', label: 'Drink menu', kind: 'url', required: false },
      { key: 'reservations', label: 'Reservations', kind: 'url', required: true },
      { key: 'takeout', label: 'Takeout / online ordering', kind: 'url', required: false },
      { key: 'events', label: 'Private events', kind: 'url', required: false },
      { key: 'delivery', label: 'Delivery', kind: 'url', required: false },
      { key: 'presentation', label: 'Food & drink presentation sheets', kind: 'url', required: false, hint: 'Photos of how each dish and drink should look. A link is fine.' },
    ],
  },
  faq: {
    id: 'faq', number: '09', title: 'FAQ', heading: 'How we answer for you.',
    clientIntro: 'These come up in DMs, comments and calls on every account. A one-line answer and who to route it to is all we need; skip anything that doesn’t apply.',
    staffIntro: 'Answer what you already know. Leave the rest for the client.',
    optional: false, scope: 'faq',
    fields: [
      { key: 'answer', label: 'Answer', kind: 'textarea', required: false },
      { key: 'contact', label: 'Route to', kind: 'text', required: false, confirmable: false },
      { key: 'email', label: 'Contact email', kind: 'email', required: false, confirmable: false },
    ],
  },
}

export const SECTION_ORDER = Object.values(SECTIONS).map((s) => s.id)

export function sectionDef(id: SectionId): SectionDef {
  return SECTIONS[id]
}
